import { useRef, useState } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import { TranslationService } from '../../services/translation.service';
import ClearButtonComponent from '../Shared/ClearButton';
import LineListShowcase from '../Shared/LineListShowcase';
import { SanitizerService } from '../../services/sanitizer.service';
import { AnalyticsService } from '../../services/analytics-service';
import type { ILine } from '../../models/line.model';
import { ClientSideStorageItems } from '../../services/client-side-storage.service';
import { LanguageService } from '../../services/language.service';
import { LineContractorApiModel } from './models/line-contractor.api-model';
import { LineHelper } from '../../helpers/line.helper';
import type { IAutocomplete } from '../Shared/AutocompleteBase';
import LineAutocomplete, { type ILineAutocompleteState } from '../Shared/LineAutocomplete';
import type { ILineContractor } from '../../models/line-contractor.model';
import GeneralError from '../Shared/GeneralError';
import ValidationInfo from '../../validation/validation-info';
import useFetch, { Status } from '../../hooks/fetch.hook';
import { TrafficInformationFeedbackService } from '../../services/traffic-information-feedback.service';

function processReceivedData(data: LineContractorApiModel[]): ILineContractor[] {
  return data?.map((d) => LineContractorApiModel.mapToBusinessModel(d)).filter((d): d is ILineContractor => d !== undefined) ?? [];
}

function LostAndFound() {
  const translationLine = TranslationService.get('lostandfound.searchlinelabel');
  const translationContactInformation = TranslationService.get('lostandfound.contactinformation');
  const translationContractorPrefix = TranslationService.get('lostandfound.contractorprefix');
  const translationPhoneHourPrefix = TranslationService.get('lostandfound.phonehourprefix');
  const translationWebsiteLinkText = TranslationService.get('lostandfound.websitelinktext');
  const translationNoLineSelectedText = TranslationService.get('lostandfound.nolineselectedtext');
  const translationSearchLinePlaceholderText = TranslationService.get('lostandfound.searchlineplaceholdertext');
  const translationSearchLineNoResultsText = TranslationService.get('lostandfound.searchlinenoresultstext') || TranslationService.get('common.autocomplete.noresults.default');

  const [lineAutocompleteState, setLineAutocompleteState] = useState<ILineAutocompleteState>({ value: undefined, validationInfo: new ValidationInfo() });
  const preventFetch = !lineAutocompleteState.value;
  const url = !preventFetch ? `/api/lost-and-found/lines/${lineAutocompleteState.value?.gid}/contractors?language=${LanguageService.getCurrentLanguage()}` : undefined;
  const [{ status: getLineContractorsStatus, data: getLineContractorsData }] = useFetch<LineContractorApiModel[]>(url, preventFetch);
  const lineAutocompleteRef = useRef<IAutocomplete>(null);

  const contractors = getLineContractorsData ? processReceivedData(getLineContractorsData) : [];

  // Hide all future lines
  function hideFutureLines(line: ILine | undefined): boolean {
    return line?.appliesFrom ? line.appliesFrom <= DateTime.now() : true;
  }

  // See NV-3700, Hide all lines for Västtrafik/Göteborgs Spårvägar Ersättningstrafik
  function hideSpecifiedLines(line: ILine): boolean {
    const gsersContractorId = 18; // Göteborgs Spårvägar Ersättningstrafik
    const vtersContractorId = 315; // Västtrafik Ersättningstrafik
    return line.contractorId !== gsersContractorId && line.contractorId !== vtersContractorId;
  }

  function onLineAutocompleteSelect(line: ILine | undefined) {
    setLineAutocompleteState({
      value: line,
      validationInfo: new ValidationInfo(),
    });
    if (line && lineAutocompleteRef.current) {
      lineAutocompleteRef.current.addSelectedItemToHistory(line);
      AnalyticsService.addEvent(AnalyticsService.LostAndFound, 'Sökalternativ - Linje', `${LineHelper.getLineNameAndDirection(line)}, ${line.gid}`);
      if (TrafficInformationFeedbackService.isEnabled()) {
        TrafficInformationFeedbackService.registerSelectedLine(line);
      }
    }
  }

  const onClearClick = () => {
    lineAutocompleteRef.current?.clear();
    setLineAutocompleteState({
      value: undefined,
      validationInfo: new ValidationInfo(),
    });
    AnalyticsService.addEvent(AnalyticsService.LostAndFound, 'Klick på återställ', '');
  };

  return (
    <div>
      <div className="lost-and-found">
        <div className="lost-and-found__line shadow-sm rounded py-sm-3 mb-4">
          <div className="mb-3 px-sm-3">
            <LineAutocomplete
              id="line-autocomplete"
              /* @ts-expect-error ts(2322) */
              ref={lineAutocompleteRef}
              labelText={translationLine}
              labelClassName="form-label"
              placeholderText={translationSearchLinePlaceholderText}
              noResultsText={translationSearchLineNoResultsText}
              historyProps={{ localStorageItem: ClientSideStorageItems.LocalStorage.LineAutocompleteHistory }}
              hideFutureLines={true}
              listAriaLabelledBy="lost-and-found-line-label"
              filterResults={(l) => hideFutureLines(l) && hideSpecifiedLines(l)}
              state={lineAutocompleteState}
              onSelect={onLineAutocompleteSelect}
            />
          </div>
          <div className="px-sm-3">
            <ClearButtonComponent onClick={onClearClick}></ClearButtonComponent>
          </div>
        </div>
        <h2>{translationContactInformation}</h2>
        {lineAutocompleteState.value && getLineContractorsStatus === Status.success && (
          <div className="lost-and-found__result-box p-3 mb-3">
            <LineListShowcase
              key={lineAutocompleteState.value.gid}
              lines={[lineAutocompleteState.value]}
              showTrainDirectionName={true}
              showNonTrainDirectionName={true}
              className="me-2 mb-3 flex-shrink-0"
            />
            {contractors.map((contractor) => (
              <div key={contractor.name} className="mb-n1">
                <div className="mb-1">
                  {translationContractorPrefix} {contractor.name}
                </div>
                {contractor.telephoneNumber && (
                  <div className="mb-1">
                    <a
                      href={`tel:${contractor.telephoneNumber}`}
                      onClick={() => AnalyticsService.addEvent(AnalyticsService.LinksCategory, AnalyticsService.PhoneNumberAction, contractor.telephoneNumber)}
                    >
                      {contractor.telephoneNumber}
                    </a>
                  </div>
                )}
                {contractor.phoneHours && (
                  <div className="mb-1">
                    {translationPhoneHourPrefix}: {contractor.phoneHours}
                  </div>
                )}
                {contractor.emailAddress && (
                  <div className="mb-1">
                    <a
                      href={`mailto:${contractor.emailAddress}`}
                      onClick={() => AnalyticsService.addEvent(AnalyticsService.LinksCategory, AnalyticsService.EmailAddressAction, contractor.emailAddress)}
                    >
                      {contractor.emailAddress}
                    </a>
                  </div>
                )}
                {contractor.website && (
                  <div className="mb-1">
                    <a href={contractor.website} onClick={() => AnalyticsService.addEvent(AnalyticsService.LinksCategory, AnalyticsService.ExternalLinkAction, contractor.website)}>
                      {translationWebsiteLinkText || contractor.website}
                    </a>
                  </div>
                )}
                {contractor.comment && <div className="mb-1">{contractor.comment}</div>}
              </div>
            ))}
          </div>
        )}
        {!lineAutocompleteState.value && <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(translationNoLineSelectedText) }}></div>}
        <GeneralError showError={getLineContractorsStatus === Status.failed} />
      </div>
    </div>
  );
}

export default LostAndFound;
